/**
 * stuburt-free-shipping-boost.js — <stuburt-ship-boost>
 *
 * One-tap "top up to free delivery" grid (markup: snippets/
 * stuburt-free-shipping-boost.liquid). Tapping a product card adds its first
 * available variant via /cart/add.js, then fires the `cart:update` event that
 * Horizon's cart-items-component listens to — so the line items, the
 * free-shipping meter and this grid all re-render server-side with the new total.
 * Once the cart clears the threshold, the snippet renders nothing, so the grid
 * removes itself on the next render.
 *
 * No client-side money: prices and the "crosses the line" logic are all Liquid.
 * This element only POSTs a variant id and relays the cart event. The add carries
 * `quiet: true` so the branded mini-cart doesn't pop a redundant confirmation over
 * the cart the customer is already looking at.
 *
 * Custom Element; single delegated listener; full teardown in disconnectedCallback
 * (the cart section re-renders aggressively and replaces this element each add).
 */

class StuburtShipBoost extends HTMLElement {
  #abort = null;
  #busy = false;

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
    this.addEventListener('keydown', this.#onKeydown, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#busy = false;
  }

  // role="button" is not a button: it gets no Enter/Space for free.
  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const pickTile = event.target.closest?.('.stuburt-boost__item--pick');
    if (!pickTile || !this.contains(pickTile)) return;
    event.preventDefault();
    event.stopPropagation();
    pickTile.querySelector('[data-quick-pick-trigger]')?.click();
  };

  #onClick = (event) => {
    // A tile for a product WITH options opens the store's size picker. The picker ships
    // its own small circular trigger, but on a tile this size that is a tiny target next
    // to a big obvious thumbnail — so the whole tile forwards to it. Clicks that already
    // landed inside the picker (its trigger, colour chips, size chips, Add) are its own
    // business and pass straight through, or the forward would toggle it shut again.
    const pickTile = event.target.closest('.stuburt-boost__item--pick');
    if (pickTile && this.contains(pickTile)) {
      if (event.target.closest('stuburt-quick-pick')) return;
      const trigger = pickTile.querySelector('[data-quick-pick-trigger]');
      if (trigger) {
        event.preventDefault();
        // Stop the ORIGINAL click here. The picker closes itself on any document-level
        // click landing outside its panel, so letting this one carry on up would open the
        // panel on the forwarded click and then immediately shut it again on the same
        // gesture — which looks exactly like "I pressed it and nothing happened".
        event.stopPropagation();
        trigger.click();
      }
      return;
    }

    const button = event.target.closest('[data-boost-add]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    this.#add(button);
  };

  async #add(button) {
    const variantId = button.getAttribute('data-boost-add');
    if (!variantId || this.#busy) return;
    this.#busy = true;
    button.classList.remove('is-error');
    button.classList.add('is-adding');
    button.disabled = true;

    try {
      const body = new FormData();
      body.set('id', variantId);
      body.set('quantity', '1');
      // Re-render the cart bubble in the same round trip; the cart section itself
      // re-fetches off the cart:update event below.
      body.append('sections', 'cart-icon-bubble');
      body.append('sections_url', window.location.pathname);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body,
        // No X-Requested-With: the store's bot protection answers cart adds carrying
        // AJAX markers with a fake 422 "sold out" (see error ledger 2026-07-05).
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('add failed');
      const data = await res.json().catch(() => ({}));

      // The cart bubble + cart section subscribe to this. `quiet` tells the branded
      // mini-cart to stay shut — the customer is already in the cart.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              source: 'product-form-component',
              itemCount: 1,
              variantId: String(variantId),
              quiet: true,
              sections: data.sections,
            },
          },
        })
      );
      // On success this element is replaced by the re-rendered cart, so leave the
      // button in its pressed state — no fallback needed.
    } catch {
      button.classList.remove('is-adding');
      button.classList.add('is-error');
      button.disabled = false;
      this.#busy = false;
    }
  }
}

if (!customElements.get('stuburt-ship-boost')) {
  customElements.define('stuburt-ship-boost', StuburtShipBoost);
}
